
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useDatabaseDiagnostics } from "@/hooks/useDatabaseDiagnostics";

export function DatabaseDiagnostics() {
  const { isLoading, diagnosticResults, runDiagnostics, fixDatabaseIssues } = useDatabaseDiagnostics();

  // Group results by category
  const groupedResults = {
    functions: {
      postgres_inspect: diagnosticResults.postgres_inspect,
      match_candidates: diagnosticResults.match_candidates,
      match_jobs: diagnosticResults.match_jobs,
      match_companies: diagnosticResults.match_companies,
    },
    extension: {
      pgvector_extension: diagnosticResults.pgvector_extension,
    },
    columns: {
      candidates_embedding: diagnosticResults.candidates_embedding,
      jobs_embedding: diagnosticResults.jobs_embedding,
      companies_embedding: diagnosticResults.companies_embedding,
    }
  };

  const renderStatusItem = (label: string, status: boolean | undefined) => (
    <div className="flex items-center justify-between border-b pb-2">
      <span>{label}</span>
      {status === undefined ? (
        <span className="text-gray-400">Not checked</span>
      ) : status ? (
        <CheckCircle className="h-5 w-5 text-green-500" />
      ) : (
        <XCircle className="h-5 w-5 text-red-500" />
      )}
    </div>
  );

  const allPassed = Object.values(diagnosticResults).every(value => value === true);
  const anyFailed = Object.values(diagnosticResults).some(value => value === false);
  const hasResults = Object.keys(diagnosticResults).length > 0;

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Database Configuration Diagnostics</CardTitle>
        <CardDescription>
          Check if your database is correctly configured for vector search
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {hasResults && (
          <>
            {allPassed && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <AlertTitle>All checks passed</AlertTitle>
                <AlertDescription>
                  Your database is correctly configured for vector search
                </AlertDescription>
              </Alert>
            )}
            
            {anyFailed && (
              <Alert className="bg-red-50 border-red-200">
                <XCircle className="h-5 w-5 text-red-500" />
                <AlertTitle>Configuration issues detected</AlertTitle>
                <AlertDescription>
                  Some components are missing or misconfigured
                </AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-6 mt-4">
              <div>
                <h3 className="text-sm font-medium mb-2">Database Functions</h3>
                <div className="space-y-2">
                  {Object.entries(groupedResults.functions).map(([key, value]) => 
                    renderStatusItem(key, value as boolean | undefined)
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium mb-2">PgVector Extension</h3>
                <div className="space-y-2">
                  {Object.entries(groupedResults.extension).map(([key, value]) => 
                    renderStatusItem(key, value as boolean | undefined)
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium mb-2">Embedding Columns</h3>
                <div className="space-y-2">
                  {Object.entries(groupedResults.columns).map(([key, value]) => 
                    renderStatusItem(key, value as boolean | undefined)
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        
        {!hasResults && (
          <div className="text-center py-4 text-gray-500">
            Run diagnostics to check your database configuration
          </div>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={runDiagnostics}
          disabled={isLoading}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Run Diagnostics
        </Button>
        
        {anyFailed && (
          <Button 
            variant="default" 
            onClick={fixDatabaseIssues}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Fix Issues
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
